import type { CSSProperties, ReactNode } from 'react';
import { GrowOnView } from './motion';

/**
 * Every chart ships with a table twin. The reference calls this the readability
 * obligation: strip the styles, or read it with a screen reader, and the
 * figures still arrive.
 */
function TableTwin({ caption, children }: { caption: string; children: ReactNode }) {
  return (
    <table className="sr-only">
      <caption>{caption}</caption>
      {children}
    </table>
  );
}

/* ------------------------------------------------------------------ */
/* Outcome band                                                        */
/* ------------------------------------------------------------------ */

const BAND = [
  { label: 'Far worse', share: 4 },
  { label: 'Much worse', share: 9 },
  { label: 'Worse', share: 16 },
  { label: 'Slightly worse', share: 24 },
  { label: 'At threshold', share: 31 },
  { label: 'Slightly better', share: 22 },
  { label: 'Better', share: 13 },
  { label: 'Much better', share: 7 },
  { label: 'Far better', share: 3 },
  { label: 'Best case', share: 2 },
];

const THRESHOLD_INDEX = 7;

export function OutcomeBand() {
  const max = Math.max(...BAND.map((b) => b.share));

  return (
    <figure className="m-0">
      <GrowOnView>
        <div
          className="flex h-32 items-end gap-1.5"
          role="img"
          aria-label="Outcome band: simulated futures grouped from worse to better, with the stated threshold falling between the eighth and ninth group."
        >
          {BAND.map((bin, i) => (
            <div key={bin.label} className="relative flex h-full flex-1 items-end">
              {i === THRESHOLD_INDEX && (
                <span
                  aria-hidden="true"
                  className="bg-amber-vivid absolute -top-3 bottom-0 -left-1 w-0.5"
                />
              )}
              <span
                className={`grow-y w-full rounded-t-sm ${
                  i <= 6
                    ? 'bg-[linear-gradient(180deg,color-mix(in_oklab,var(--blue-vivid)_72%,white),var(--blue-vivid))]'
                    : 'bg-line-strong'
                }`}
                style={{ height: `${(bin.share / max) * 100}%`, '--bar-index': i } as CSSProperties}
              />
            </div>
          ))}
        </div>
      </GrowOnView>
      <div className="font-mono text-faint mt-3 flex justify-between text-[0.625rem] tracking-[0.16em] uppercase">
        <span>Worse</span>
        <span className="text-accent font-bold">Your threshold</span>
        <span>Better</span>
      </div>
      <TableTwin caption="Outcome band — share of simulated futures by band">
        <tbody>
          {BAND.map((bin) => (
            <tr key={bin.label}>
              <th scope="row">{bin.label}</th>
              <td>{bin.share}%</td>
            </tr>
          ))}
        </tbody>
      </TableTwin>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Sensitivity ranking                                                 */
/* ------------------------------------------------------------------ */

const SENSITIVITY = [
  { name: 'Paid conversion on the acquisition channel', weight: 100 },
  { name: 'Contribution margin at target volume', weight: 64 },
  { name: 'Time from signup to first value', weight: 38 },
  { name: 'Retention at month twelve', weight: 18 },
];

export function SensitivityBars() {
  return (
    <figure className="m-0">
      <GrowOnView>
        <ol className="flex flex-col gap-4">
          {SENSITIVITY.map((row, i) => (
            <li key={row.name}>
              <div className="flex items-baseline justify-between gap-4 text-sm">
                <span className="font-medium">{row.name}</span>
                {i === 0 && <span className="text-faint shrink-0 text-xs">highest</span>}
                {i === SENSITIVITY.length - 1 && (
                  <span className="text-faint shrink-0 text-xs">lowest</span>
                )}
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface-2">
                <span
                  className="grow-x block h-full rounded-full bg-brand"
                  style={{ width: `${row.weight}%`, animationDelay: `${i * 90}ms` }}
                />
              </div>
            </li>
          ))}
        </ol>
      </GrowOnView>
      <TableTwin caption="Sensitivity — how much resolving each assumption moves the outcome">
        <tbody>
          {SENSITIVITY.map((row) => (
            <tr key={row.name}>
              <th scope="row">{row.name}</th>
              <td>{row.weight} of 100</td>
            </tr>
          ))}
        </tbody>
      </TableTwin>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Kill-zone quadrant                                                  */
/* ------------------------------------------------------------------ */

const KILL_ZONE = [
  { name: 'Paid conversion', x: 22, y: 78, tier: 'high' },
  { name: 'Contribution margin', x: 58, y: 46, tier: 'mid' },
  { name: 'Time to first value', x: 78, y: 30, tier: 'mid' },
  { name: 'Retention at month twelve', x: 30, y: 16, tier: 'low' },
];

const DOT: Record<string, string> = {
  high: 'bg-amber-vivid ring-4 ring-amber-vivid/25',
  mid: 'bg-blue-vivid ring-4 ring-blue-vivid/25',
  low: 'bg-emerald-vivid ring-4 ring-emerald-vivid/25',
};

export function KillZone() {
  return (
    <figure className="m-0">
      <div
        className="border-line relative aspect-[16/9] w-full overflow-hidden rounded-xl bg-[linear-gradient(160deg,color-mix(in_oklab,var(--blue-vivid)_10%,transparent),transparent)]"
        role="img"
        aria-label="Kill-zone quadrant plotting distance to the kill-line against the probability the assumption is wrong. Paid conversion sits top-left, the quadrant that ends ventures."
      >
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-[linear-gradient(to_right,var(--grid-line)_1px,transparent_1px),linear-gradient(to_bottom,var(--grid-line)_1px,transparent_1px)] bg-[size:14.28%_25%]"
        />
        <span
          aria-hidden="true"
          className="bg-amber-vivid/10 border-line absolute top-0 left-0 h-1/2 w-1/2 border-r border-b border-dashed"
        />
        {KILL_ZONE.map((p, i) => (
          <span
            key={p.name}
            className={`absolute size-2.5 -translate-x-1/2 translate-y-1/2 rounded-full ${DOT[p.tier]} animate-pulse-dot`}
            style={{ left: `${p.x}%`, bottom: `${p.y}%`, animationDelay: `${i * 400}ms` }}
          />
        ))}
      </div>
      <div className="font-mono text-faint mt-3 flex justify-between text-[0.625rem] tracking-[0.16em] uppercase">
        <span>Distance to kill-line →</span>
        <span>↑ Probability wrong</span>
      </div>
      <TableTwin caption="Kill-zone — distance to kill-line against probability wrong">
        <tbody>
          {KILL_ZONE.map((p) => (
            <tr key={p.name}>
              <th scope="row">{p.name}</th>
              <td>distance {p.x} of 100</td>
              <td>probability wrong {p.y} of 100</td>
            </tr>
          ))}
        </tbody>
      </TableTwin>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Audit verdict bar                                                   */
/* ------------------------------------------------------------------ */

const VERDICTS = [
  { name: 'Contributing', share: 47.1, className: 'bg-brand' },
  { name: 'Duplicate', share: 26.3, className: 'bg-blue-vivid/45' },
  { name: 'Inert', share: 26.6, className: 'bg-line-strong' },
];

export function VerdictBar() {
  return (
    <GrowOnView>
      <div
        className="flex h-10 w-full overflow-hidden rounded-full border border-line"
        role="img"
        aria-label="Audit verdicts across 20,821 live and callable swarm classes: 47.1 percent contributing, 26.3 percent duplicate, 26.6 percent inert."
      >
        {VERDICTS.map((v, i) => (
          <span
            key={v.name}
            className={`grow-x ${v.className}`}
            style={{ width: `${v.share}%`, animationDelay: `${i * 140}ms` }}
          />
        ))}
      </div>
    </GrowOnView>
  );
}

export const AUDIT_VERDICTS = [
  {
    name: 'Contributing',
    count: '9,807',
    share: '47.1%',
    meaning:
      'Real algorithm, distinct from its siblings, and demonstrably changes the output when the input changes.',
    emphasis: 'The only number we publish as contributing.',
  },
  {
    name: 'Duplicate',
    count: '5,468',
    share: '26.3%',
    meaning: 'Real algorithm, but byte-identical to a sibling carrying a version suffix.',
  },
  {
    name: 'Inert',
    count: '5,546',
    share: '26.6%',
    meaning: 'Runs without error and never changes the reply.',
  },
  { name: 'Stubs', count: '0', share: '—', meaning: 'Nothing unimplemented was found.' },
  {
    name: 'Errors',
    count: '0',
    share: '—',
    meaning:
      'Every class executed without raising. Two abstract base classes were excluded as not callable.',
  },
] as const;

/* ------------------------------------------------------------------ */
/* Threshold wave (contact hero)                                       */
/* ------------------------------------------------------------------ */

export function ThresholdWave() {
  return (
    <svg
      viewBox="0 0 420 260"
      className="h-auto w-full"
      role="img"
      aria-labelledby="wave-title wave-desc"
    >
      <title id="wave-title">Simulated futures against a walk-away threshold</title>
      <desc id="wave-desc">
        Two trajectories oscillate across a horizontal baseline while a vertical marker shows the
        month by which the stated threshold must hold.
      </desc>
      <defs>
        <linearGradient id="wave-stroke" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#1f6feb" />
          <stop offset="55%" stopColor="#1189a8" />
          <stop offset="100%" stopColor="#12a870" />
        </linearGradient>
      </defs>

      <g stroke="var(--grid-line)" strokeWidth="1">
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <line key={`v${i}`} x1={30 + i * 60} y1="20" x2={30 + i * 60} y2="240" />
        ))}
        {[0, 1, 2, 3].map((i) => (
          <line key={`h${i}`} x1="30" y1={40 + i * 60} x2="390" y2={40 + i * 60} />
        ))}
      </g>

      <path
        data-draw=""
        d="M30 150 C 75 90, 105 90, 150 150 S 225 210, 270 150 S 345 90, 390 118"
        fill="none"
        stroke="url(#wave-stroke)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        data-draw=""
        d="M30 170 C 80 190, 120 130, 170 168 S 250 200, 300 156 S 360 140, 390 162"
        fill="none"
        stroke="color-mix(in oklab, var(--muted) 55%, transparent)"
        strokeWidth="1.5"
        strokeDasharray="4 5"
      />
      <line x1="252" y1="46" x2="252" y2="236" stroke="var(--amber-vivid)" strokeWidth="1.5" />
      <text
        x="252"
        y="36"
        textAnchor="middle"
        fill="currentColor"
        fontFamily="JetBrains Mono, monospace"
        fontSize="11"
        letterSpacing="1.4"
      >
        WALK-AWAY THRESHOLD
      </text>
    </svg>
  );
}
